import { createSelector } from 'reselect';

import { ElaboratedTicketGroup } from 'types';
import { Features } from '@tock/coretypes';
import { Filter } from 'state/calendarFilter';
import { IServiceObject } from 'utils/getServiceDayDetails';
import { Party } from 'types/service';
import { StoreType } from '@tock/shared/utils/connect';
import { calendarServiceSelector } from 'selectors/serviceSelectors';
import { fastParseDateTime } from 'utils/timeUtils';
import { featuresSelector, flexSelector } from 'selectors/featuresSelector';
import { filter, forEach, groupBy, includes, isEmpty, range, some } from 'es-toolkit/compat';
import { filterBySelectedPartyListServices } from 'utils/service/partyListUtils';
import { getInferredServiceTypeString } from 'utils/partyUtils';
import { getTagsForProfile } from 'utils/tagUtils';
import { partiesByOriginalPurchaseIdSelector } from './partiesById';
import { selectedPartyListServicesSelector } from 'selectors/calendarFilterSelectors';
import activeServicesSelector from 'selectors/service/activeServicesSelector';
import serviceObjectsForFilteringByDaySelector from 'selectors/service/serviceObjectsForFilteringByDaySelector';

export interface TicketGroupsByDateTime {
  [ids: string]: ElaboratedTicketGroup[];
}

function organizeCalendarByDateTime(ticketGroups: ElaboratedTicketGroup[]) {
  const dateTimes: { [id: string]: ElaboratedTicketGroup[] } = {};
  const organizedCalendar = groupBy(ticketGroups, (ticketGroup) => {
    dateTimes[ticketGroup.dateTime] = [];
    return ticketGroup.dateTime;
  });

  const dateTimeKeys = Object.keys(dateTimes).sort();
  const sortedDateTimes: { [id: string]: ElaboratedTicketGroup[] } = {};
  forEach(dateTimeKeys, (dateTime) => {
    sortedDateTimes[dateTime] = [];
  });

  forEach(organizedCalendar, (ticketGroups, dateTime) => {
    sortedDateTimes[dateTime] = ticketGroups;
  });

  return sortedDateTimes;
}

const alwaysTrue = (_: ElaboratedTicketGroup) => true;
type NumberField = number | null | undefined;
type StringField = string | null | undefined;

const createSizeMatcher = (params: Filter) => {
  if (params.size.length === 0) {
    return alwaysTrue;
  }

  const hasTenFilter = includes(params.size, 10);
  const matches = (value: NumberField) =>
    includes(params.size, value) || (hasTenFilter && value && value > 10);

  return (ticketGroup: ElaboratedTicketGroup) => {
    const minPurchaseSize: number = +ticketGroup.minPurchaseSize;
    const maxPurchaseSize: number = +ticketGroup.maxPurchaseSize;

    if (ticketGroup.tableVariety === 'TABLE') {
      switch (ticketGroup.state) {
        case 'AVAILABLE':
          return some(range(minPurchaseSize, maxPurchaseSize + 1), matches);
        case 'LOCKED':
          return some(
            ticketGroup.ticketSubset,
            (subset) => subset.state === 'LOCKED' && matches(subset.ticketCount)
          );
        case 'HELD':
          return some(
            ticketGroup.ticketSubset,
            (subset) => subset.state === 'HELD' && matches(subset.ticketCount)
          );
        case 'SOLD':
          return some(ticketGroup.purchaseDetail, (purchase) => matches(purchase.ticketCount));
        default:
          return false;
      }
    } else {
      return (
        some(range(minPurchaseSize, maxPurchaseSize + 1), matches) ||
        some(ticketGroup.ticketSubset, (subset) => matches(subset.ticketCount)) ||
        some(ticketGroup.purchaseDetail, (purchase) => matches(purchase.ticketCount))
      );
    }
  };
};

const createTicketTypeMatcher = (params: Filter) => {
  if (params.ticketTypeId.length === 0) {
    return alwaysTrue;
  }

  const matches = (value: NumberField) => includes(params.ticketTypeId, value);

  return (ticketGroup: ElaboratedTicketGroup) => {
    return ticketGroup.purchaseDetail.length > 0
      ? some(ticketGroup.purchaseDetail, (purchase) => matches(purchase.ticketTypeId))
      : matches(ticketGroup.ticketType[0].id);
  };
};

const createTicketOptionMatcher = (params: Filter) => {
  if (params.ticketOptionId.length === 0) {
    return alwaysTrue;
  }

  const matches = (value: NumberField) => includes(params.ticketOptionId, value);

  return (ticketGroup: ElaboratedTicketGroup) => {
    return ticketGroup.purchaseDetail.length > 0
      ? some(ticketGroup.purchaseDetail, (purchase) =>
          some(purchase.purchasedOption, (option) => matches(option.id))
        )
      : some(ticketGroup.ticketOption, (option) => matches(option.id));
  };
};

const createGuestTagMatcher = (params: Filter) => {
  if (params.guestTag.length === 0) {
    return alwaysTrue;
  }

  const matches = (value: StringField) => includes(params.guestTag, value);

  return (ticketGroup: ElaboratedTicketGroup) => {
    return some(ticketGroup.purchaseDetail, (purchase) =>
      some(getTagsForProfile(purchase.dinerGuestProfile), matches)
    );
  };
};

const createVisitTagMatcher = (params: Filter) => {
  if (params.visitTag.length === 0) {
    return alwaysTrue;
  }
  const matches = (value: StringField) => includes(params.visitTag, value);

  return (ticketGroup: ElaboratedTicketGroup) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return some(ticketGroup.purchaseDetail, (purchase) => some(purchase.visit!.tag, matches));
  };
};

const createPurchaseStateMatcher = (params: Filter) => {
  if (params.purchaseState.length === 0) {
    return alwaysTrue;
  }
  const matches = (value: StringField) => includes(params.purchaseState, value);

  return (ticketGroup: ElaboratedTicketGroup) => {
    return (
      matches(ticketGroup.state) ||
      some(ticketGroup.calendarItems, (calendarItem) => matches(calendarItem.state))
    );
  };
};

const createEditedStatusMatcher = (params: Filter) => {
  if (params.editedStatus === 'ALL') {
    return alwaysTrue;
  }

  const edited = params.editedStatus === 'YES';
  return (ticketGroup: ElaboratedTicketGroup) => {
    return ticketGroup.isPristine === !edited;
  };
};

const createUnansweredQuestionsMatcher = (params: Filter) => {
  if (!params.isUnansweredFilterOn) {
    return alwaysTrue;
  }

  return (ticketGroup: ElaboratedTicketGroup) => {
    return some(
      ticketGroup.purchaseDetail,
      (purchase) => !purchase.question || purchase.question.length === 0
    );
  };
};

const createPartyStatusMatcher = (params: Filter) => {
  if (!params.partyStatuses || isEmpty(params.partyStatuses)) {
    return alwaysTrue;
  }
  return (ticketGroup: ElaboratedTicketGroup, parties: Dictionary<Party>) => {
    return some(ticketGroup.purchaseDetail, (purchase) => {
      if (purchase.originalPurchaseId) {
        const party = parties[purchase.originalPurchaseId];
        if (party) {
          const stateString = party.state + '-' + (party.secondaryState || '');
          return includes(params.partyStatuses, stateString);
        }
      }
      return false;
    });
  };
};

const createExclusivityMatcher = (params: Filter) => {
  if (!params.exclusives || isEmpty(params.exclusives)) {
    return alwaysTrue;
  }

  return (ticketGroup: ElaboratedTicketGroup) => {
    return (
      includes(params.exclusives, ticketGroup.exclusivity) ||
      includes(params.exclusives, ticketGroup.ticketType?.[0].exclusivity)
    );
  };
};

const serviceTypeMatcher = (
  ticketGroup: ElaboratedTicketGroup,
  selectedPartyListServices: number[],
  serviceInfoForServiceDay: IServiceObject[],
  selectedServices: string[],
  activeServices: string[],
  features: Features
) => {
  // Filter based on service type to support public hours filtering in snapshot & timeline for slot businesses.
  // SLOT_PUBLIC_HOURS_FILTER_FOR_TICKET_GROUPS was created in an attempt to fix an issue reported by a customer
  // https://tock.atlassian.net/browse/TK-11589 - we can consider launching more broadly after confirming the fix.
  if (
    features.PUBLIC_HOURS_BASED_SERVICE_FILTER &&
    features.SLOT_PUBLIC_HOURS_FILTER_FOR_TICKET_GROUPS
  ) {
    if (serviceInfoForServiceDay.length === 0) {
      return true;
    }

    const serviceTypeString = getInferredServiceTypeString(
      serviceInfoForServiceDay,
      fastParseDateTime(ticketGroup.dateTime)
    );

    return filterBySelectedPartyListServices(selectedPartyListServices, serviceTypeString);
  } else {
    // We phrase this check this way because in rare circumstances, customers can create
    // bookings from the timeline that don't correspond to the actually offered services.
    // Doing only the includes() check below will trip on that and will filter out
    // bookings that the user probably does want to see.
    //
    // So, we apply this filtering iff the number of selected services is less
    // than the number of active services. This will head off the problem in the most
    // obvious cases.
    if (
      selectedServices.length < activeServices.length &&
      !includes(selectedServices, ticketGroup.serviceType)
    ) {
      return false;
    }
  }

  return true;
};

export const calendarTicketGroupsSelector = (state: StoreType) => state.calendar.calendar;

export const filteredTicketGroupsSelector = createSelector(
  calendarTicketGroupsSelector,
  calendarServiceSelector,
  (state: StoreType) => state.calendarFilter.queryParams,
  (state: StoreType) => state.calendarFilter.hasFiltersApplied,
  activeServicesSelector,
  partiesByOriginalPurchaseIdSelector,
  flexSelector,
  selectedPartyListServicesSelector,
  serviceObjectsForFilteringByDaySelector,
  featuresSelector,
  (
    calendar,
    selectedServices,
    params,
    hasFiltersApplied,
    activeServices,
    partiesByOriginalPurchaseId,
    isFlex,
    selectedPartyListServices,
    serviceInfoForServiceDay,
    features
  ): ElaboratedTicketGroup[] => {
    if (isFlex || (selectedServices.length === activeServices.length && !hasFiltersApplied)) {
      return calendar;
    }

    const sizeMatcher = createSizeMatcher(params);
    const ticketTypeMatcher = createTicketTypeMatcher(params);
    const ticketOptionMatcher = createTicketOptionMatcher(params);
    const guestTagMatcher = createGuestTagMatcher(params);
    const visitTagMatcher = createVisitTagMatcher(params);
    const purchaseStateMatcher = createPurchaseStateMatcher(params);
    const editedStatusMatcher = createEditedStatusMatcher(params);
    const unansweredMatcher = createUnansweredQuestionsMatcher(params);
    const partyStatusMatcher = createPartyStatusMatcher(params);
    const exclusivityMatcher = createExclusivityMatcher(params);

    return filter(calendar, (ticketGroup: ElaboratedTicketGroup) => {
      if (
        !serviceTypeMatcher(
          ticketGroup,
          selectedPartyListServices,
          serviceInfoForServiceDay ?? [],
          selectedServices,
          activeServices,
          features
        )
      ) {
        return false;
      }
      if (!sizeMatcher(ticketGroup)) {
        return false;
      }
      if (!ticketTypeMatcher(ticketGroup)) {
        return false;
      }
      if (!ticketOptionMatcher(ticketGroup)) {
        return false;
      }
      if (!guestTagMatcher(ticketGroup)) {
        return false;
      }
      if (!visitTagMatcher(ticketGroup)) {
        return false;
      }
      if (!purchaseStateMatcher(ticketGroup)) {
        return false;
      }
      if (!editedStatusMatcher(ticketGroup)) {
        return false;
      }
      if (!unansweredMatcher(ticketGroup)) {
        return false;
      }
      if (!partyStatusMatcher(ticketGroup, partiesByOriginalPurchaseId)) {
        return false;
      }
      if (!exclusivityMatcher(ticketGroup)) {
        return false;
      }
      return true;
    });
  }
);

export const ticketGroupsByDateTimeSelector = createSelector(
  filteredTicketGroupsSelector,
  organizeCalendarByDateTime
);

export const unfilteredTicketGroupsByDateTimeSelector = createSelector(
  calendarTicketGroupsSelector,
  organizeCalendarByDateTime
);

export const serviceTypesByDateTimeSelector = createSelector(
  ticketGroupsByDateTimeSelector,
  (ticketGroupsByDateTime: TicketGroupsByDateTime): { [dateTime: string]: string } => {
    const serviceTypesByDateTime: { [dateTime: string]: string } = {};
    forEach(ticketGroupsByDateTime, (elaboratedTicketGroups) => {
      forEach(elaboratedTicketGroups, (elaboratedTicketGroup: ElaboratedTicketGroup) => {
        if (!elaboratedTicketGroup.finishedByTime) {
          return;
        }
        forEach(elaboratedTicketGroup.calendarItems, (calendarItem) => {
          if (calendarItem.state !== 'TIME_FLEXIBLE') {
            return;
          }
          serviceTypesByDateTime[calendarItem.dateTime || elaboratedTicketGroup.dateTime] =
            elaboratedTicketGroup.serviceType;
        });
      });
    });
    return serviceTypesByDateTime;
  }
);
